/**
 * Settings page
 */

import { Link } from 'react-router-dom';
import { UserButton, useUser } from '@clerk/clerk-react';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@frameit/ui';
import { useAuthStore } from '../stores/authStore';

// Check if Clerk is configured
const hasClerk = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// Dev user for when Clerk is not configured
const devUser = {
  firstName: 'Dev',
  lastName: 'User',
  emailAddresses: [{ emailAddress: 'dev@localhost' }],
};

/** Settings page component */
export function SettingsPage() {
  // Always call useUser hook (rules of hooks), but only use its result if Clerk is configured
  const clerkUser = useUser();
  const user = hasClerk ? clerkUser.user : devUser;
  const tierLimits = useAuthStore((s) => s.getTierLimits());
  const usage = useAuthStore((s) => s.usage);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="h-14 border-b flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <Link to="/projects" className="font-semibold">
            FrameIt
          </Link>
          <span className="text-muted-foreground">/</span>
          <span>Settings</span>
        </div>
        {hasClerk ? (
          <UserButton afterSignOutUrl="/auth/sign-in" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
            D
          </div>
        )}
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto p-6 space-y-6">
        {/* Account */}
        <Card>
          <CardHeader>
            <CardTitle>Account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center text-2xl">
                {user?.firstName?.[0] || user?.emailAddresses[0]?.emailAddress[0] || '?'}
              </div>
              <div>
                <p className="font-medium">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-sm text-muted-foreground">
                  {user?.emailAddresses[0]?.emailAddress}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription */}
        <Card>
          <CardHeader>
            <CardTitle>Subscription</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Free Plan</p>
                <p className="text-sm text-muted-foreground">Basic features with watermark</p>
              </div>
              <Button>Upgrade to Pro</Button>
            </div>

            <div className="border-t pt-4 space-y-2">
              <h4 className="text-sm font-medium">Pro Benefits</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• 100 projects (vs 3)</li>
                <li>• 8K export resolution (vs 2K)</li>
                <li>• Video export</li>
                <li>• Premium templates</li>
                <li>• No watermark</li>
                <li>• Unlimited exports</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Usage */}
        <Card>
          <CardHeader>
            <CardTitle>Usage</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Projects</span>
                  <span>
                    {usage?.projectCount || 0} / {tierLimits.maxProjects}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary"
                    style={{
                      width: `${((usage?.projectCount || 0) / tierLimits.maxProjects) * 100}%`,
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Storage</span>
                  <span>
                    {formatBytes(usage?.storageUsed || 0)} / {formatBytes(tierLimits.maxStorage)}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary"
                    style={{
                      width: `${((usage?.storageUsed || 0) / tierLimits.maxStorage) * 100}%`,
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Exports this month</span>
                  <span>
                    {usage?.exportsThisMonth || 0} /{' '}
                    {tierLimits.monthlyExports === 0 ? '∞' : tierLimits.monthlyExports}
                  </span>
                </div>
                {tierLimits.monthlyExports > 0 && (
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary"
                      style={{
                        width: `${((usage?.exportsThisMonth || 0) / tierLimits.monthlyExports) * 100}%`,
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Danger zone */}
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-destructive">Danger Zone</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Delete Account</p>
                <p className="text-sm text-muted-foreground">
                  Permanently delete your account and all data
                </p>
              </div>
              <Button variant="destructive" size="sm">
                Delete Account
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

/** Format bytes to human readable */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
